import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange,
  getOptionLabel,
  getOptionValue,
  loading = false,
  helperText,
  maxSelections,
  disabled = false,
  filterLimit
}) => {
  const filterOptions = useMemo(
    () => createFilterOptions(filterLimit ? { limit: filterLimit } : {}),
    [filterLimit]
  );

  const handleChange = (_, newValue) => {
    if (maxSelections && newValue.length > maxSelections) {
      return;
    }
    onChange(newValue);
  };

  return (
    <Box sx={{ minWidth: 240 }}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedValues}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) =>
          getOptionValue(option) === getOptionValue(value)
        }
        filterOptions={filterOptions}
        limitTags={3}
        disabled={disabled}
        loading={loading}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {getOptionLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search..."
            helperText={
              helperText ||
              (maxSelections ? `Select up to ${maxSelections}` : undefined)
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </Box>
  );
};

MultiSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedValues: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  loading: PropTypes.bool,
  helperText: PropTypes.string,
  maxSelections: PropTypes.number,
  disabled: PropTypes.bool,
  filterLimit: PropTypes.number
};

MultiSelectDropdown.defaultProps = {
  getOptionLabel: (option) => option.label ?? option.name ?? '',
  getOptionValue: (option) => option.value ?? option._id ?? option.id,
  loading: false,
  helperText: undefined,
  maxSelections: undefined,
  disabled: false,
  filterLimit: undefined
};

export default MultiSelectDropdown;
